import { type ApolloError, gql, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '../../types';
import { ProductionTableView } from './ProductionTableView';
import type { AssetProductionTableResponse, ProductionData, TextFieldsData } from './types';

const QUERY = gql`
  query AssetProductionTable($input: AssetProductionTableInput!) {
    assetProductionTable(input: $input) {
      year
      oilProductionsSum
      pdpOilProductionsSum
      udOilProductionsSum
      condensateProductionsSum
      pdpCondensateProductionsSum
      udCondensateProductionsSum
      liquidsProductionsSum
      gasProductionsSum
      pdpGasProductionsSum
      udGasProductionsSum
      wellheadGasProductionsSum
      totalProductionsSum
      growthRate
      gasOilRatio
      gasMonetizationPercentage
      salesGasProductionsSum
      salesGasBoeSum
    }
  }
`;

export const COMPONENT_ID = 'production_table';

type UseDataReturn = {
  productionData: ProductionData[] | null;
  error: ApolloError | undefined;
  loading: boolean;
  textFields: TextFieldsData;
  onTextFieldsChange: (textFields: TextFieldsData) => void;
};

const useData = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: UseDataType<ProductionData[]>): UseDataReturn => {
  const [productionData, setProductionData] = useState<ProductionData[] | null>(
    persistedData || null,
  );

  const [textFields, setTextFields] = useState<TextFieldsData>({
    textField1: '',
    textField2: '',
  });

  // Update productionData when persistedData changes
  useEffect(() => {
    if (persistedData) {
      setProductionData(persistedData);
    }
  }, [persistedData]);
  // Extract asset_id from portfolio
  const assetId = portfolio?.assets?.[0];

  const { loading, error } = useQuery<AssetProductionTableResponse>(QUERY, {
    variables: {
      input: {
        portfolio,
      },
    },
    skip: !assetId || !!(persistedData && persistedData.length > 0),
    fetchPolicy: 'network-only', // Force network request to get fresh data
    onCompleted: response => {
      if (response?.assetProductionTable) {
        const data = response.assetProductionTable;
        setProductionData(data);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data: data,
          });
        }
      }
    },
  });

  const onTextFieldsChange = (newTextFields: TextFieldsData) => {
    setTextFields(newTextFields);
    // Text fields are not persisted in this simplified version
  };

  return { productionData, error, loading, textFields, onTextFieldsChange };
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
  sx,
}: ComponentEditProps<ProductionData[]>) => {
  const { productionData, loading, error, textFields, onTextFieldsChange } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  // Handle loading and error states
  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <ProductionTableView
      sx={sx || {}}
      data={productionData || []}
      textFields={textFields}
      onTextFieldsChange={onTextFieldsChange}
    />
  );
};

const ComponentView = ({ data }: ComponentViewProps<ProductionData[]>) => {
  if (!data) return null;

  // Handle different data structures - if data is wrapped in an object, extract the array
  const productionData = Array.isArray(data) ? data : [];

  // If no data is available, show a placeholder message
  if (!productionData || productionData.length === 0) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
        Production Table - No data available
      </div>
    );
  }

  return (
    <ProductionTableView
      data={productionData}
      textFields={{ textField1: '', textField2: '' }}
      onTextFieldsChange={() => {}} // No-op for view mode
      isPreview={true}
    />
  );
};

export const ProductionTableComponent: ComponentDefinition<ProductionData[]> = {
  id: COMPONENT_ID,
  name: 'Production Table',
  type: SmartReportComponentTypesEnum.Table,
  reportTypes: [SmartReportTypeEnum.Asset], // Mainly for Asset reports
  Component: ComponentEdit,
  ComponentView,
};
